import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { selectDictionary } from 'features/dictionaries/dictionariesSelectors';
import {
  selectIsJobseekersLoading,
  selectJobseekersFacets
} from 'features/jobseekers/jobseekersSelectors';
import { selectAppLocale } from 'features/locale/localeSelectors';

import { useFilters } from 'hooks/useFilters';
import { useTranslatedCountriesOptions } from 'hooks/useTranslatedCountriesOptions';

import formatAndOrderData from 'utils/formatAndOrderData';

import { JOBSEEKERS_COUNTRIES } from 'constants/endpoints';
import { AppModules, Dictionaries, FilterFields } from 'constants/enums';
import {
  JOBSEEKERS_ADDITIONAL_FACETS_CONFIG,
  JOBSEEKERS_DEFAULT_FACETS_CONFIG
} from 'constants/facets';
import languagesOrder from 'constants/languagesOrder';

import FilterWrapper from './common/FilterWrapper';
import SelectCommon from './common/SelectCommon/SelectCommon';
import CountrySelect from './components/CountrySelect';
import CountryStateCitySelect from './components/CountryStateCitySelect';
import SkillsetSelect from './components/SkillsetSelect';
import { SelectValue } from './filter.types';
import useFilterAccess from './hooks/useFilterAccess';
import filterFacetKeys from './utils/filterFacetKeys';

const JobseekersFilters = () => {
  const { t } = useTranslation();
  const { hasAccessToFilter } = useFilterAccess('jobseekerFilters');
  const {
    filters,
    updateFilter,
    isLoading: isFiltersLoading
  } = useFilters(AppModules.Jobseekers);
  const isJobseekersLoading = useSelector(selectIsJobseekersLoading);

  const facets = useSelector(selectJobseekersFacets);

  const filteredFacets = filterFacetKeys(facets, [
    ...JOBSEEKERS_DEFAULT_FACETS_CONFIG.map(({ key }) => key),
    ...JOBSEEKERS_ADDITIONAL_FACETS_CONFIG.map(({ key }) => key)
  ]);

  const locale = useSelector(selectAppLocale);

  const { country, state, city, citizenship, skillset, education, languages } =
    filters;

  const educationOptions = useSelector(
    selectDictionary(Dictionaries.Education)
  );

  const languagesDict = useSelector(selectDictionary(Dictionaries.Language));

  const languagesOptions = formatAndOrderData(languagesDict, languagesOrder);

  const { options: countries, isLoading: isCountriesLoading } =
    useTranslatedCountriesOptions({
      url: JOBSEEKERS_COUNTRIES,
      locale
    });

  const handleCitizenshipChange = (value: SelectValue) => {
    updateFilter(FilterFields.Citizenship, Array.isArray(value) ? value : []);
  };

  const handleSkillsetChange = (value: SelectValue) => {
    updateFilter(FilterFields.Skillset, Array.isArray(value) ? value : []);
  };

  const handleEducationChange = (value: SelectValue) => {
    updateFilter(FilterFields.Education, Array.isArray(value) ? value : []);
  };

  const handleLanguagesChange = (value: SelectValue) => {
    updateFilter(FilterFields.Languages, Array.isArray(value) ? value : []);
  };

  const handleCountryChange = (value: string | undefined) => {
    updateFilter(FilterFields.Country, value);
  };

  const handleCityChange = (value: string | undefined) => {
    updateFilter(FilterFields.City, value);
  };

  const handleStateChange = (value: string | undefined) => {
    updateFilter(FilterFields.State, value);
  };

  return (
    <FilterWrapper>
      <SkillsetSelect
        value={skillset}
        onChange={handleSkillsetChange}
        hasAccessToFilter={hasAccessToFilter('skillset')}
        disabled={isJobseekersLoading || isFiltersLoading}
        facets={filteredFacets.skillsets}
      />
      <SelectCommon
        title={t('filters.jobseekers.education.title')}
        value={education}
        options={educationOptions}
        onChange={handleEducationChange}
        placeholder={t('filters.jobseekers.education.placeholder')}
        showSearch
        hasAccessToFilter={hasAccessToFilter('education')}
        disabled={isJobseekersLoading || isFiltersLoading}
        facets={filteredFacets.education}
      />
      <SelectCommon
        title={t('filters.jobseekers.languages.title')}
        value={languages}
        options={languagesOptions}
        onChange={handleLanguagesChange}
        placeholder={t('filters.jobseekers.languages.placeholder')}
        showSearch
        hasAccessToFilter={hasAccessToFilter('languages')}
        disabled={isJobseekersLoading || isFiltersLoading}
        facets={filteredFacets.languages}
      />
      <CountrySelect
        title={t('filters.jobseekers.citizenship.title')}
        value={citizenship}
        onChange={handleCitizenshipChange}
        placeholder={t('filters.jobseekers.citizenship.placeholder')}
        countries={countries}
        isLoading={isCountriesLoading}
        hasAccessToFilter={hasAccessToFilter('citizenship')}
        disabled={isJobseekersLoading || isFiltersLoading}
        facets={filteredFacets.citizenship}
      />
      <CountryStateCitySelect
        customCountries={countries}
        countryValue={country as string}
        cityValue={city}
        stateValue={state}
        onChangeCountry={handleCountryChange}
        onChangeState={handleStateChange}
        onChangeCity={handleCityChange}
        hasAccessToFilter={hasAccessToFilter}
        countryTitle="common.label.jobseekerCountry"
        stateTitle="common.label.jobseekerState"
        cityTitle="common.label.jobseekerCity"
        countryPlaceholder="common.label.selectJobseekerCountry"
        statePlaceholder="common.label.selectJobseekerState"
        cityPlaceholder="common.label.selectJobseekerCity"
        disabled={isJobseekersLoading || isFiltersLoading}
        countryFacets={filteredFacets.country}
        stateFacets={filteredFacets.state}
        cityFacets={filteredFacets.city}
      />
    </FilterWrapper>
  );
};

export default JobseekersFilters;
